/**
 * Starters for the GLOBAL panel: the same derivation Lane A's page uses, with
 * the current screen added as a second input.
 *
 * This file EXTENDS `../suggestions.ts`; it does not restate it. The general
 * catalog, its ordering, its cap, and — above all — its three rules stay in
 * one place and are called into here:
 *
 *   1. NOT KNOWN YET IS NOT ZERO. An unknown tool list yields nothing at all,
 *      screen or no screen. A starter offered on the strength of a route while
 *      the caller's authority is still in flight is the exact invention the
 *      page refuses to make.
 *   2. FEWER, NEVER FILLER. A screen with no honestly answerable question
 *      contributes NO starters and falls through to the general list. This is
 *      not hypothetical: `/mileage` is a real screen with a real typed context
 *      and there is no advertised tool that can answer a mileage question, so
 *      it has no entry below. "How has mileage trended?" would be a promise
 *      the executor cannot keep, and the person would read the refusal as the
 *      assistant being broken.
 *   3. AUTHORITY, NOT ROUTE, PICKS THE PHRASING. Every candidate is gated on
 *      a tool the SERVER advertised for this caller, and where the same tool
 *      answers two different questions depending on reach, `can()` chooses
 *      between them exactly as the general catalog does. Being able to OPEN a
 *      worker profile is not authority to ask organization-shaped questions
 *      about the worker on it — screen visibility is never authority, here as
 *      on the wire.
 *
 * Screen starters lead, then the general catalog fills what is left of the
 * cap. That ordering is the whole point of the panel: opened from a worker
 * profile, the first thing offered should be about that worker.
 */

import {
  deriveSuggestions,
  MAX_SUGGESTIONS,
  type Suggestion,
  type SuggestionInput,
} from '../suggestions.js';
import type { ScreenContext, ScreenKind } from './screenContext.js';

export interface ScreenSuggestionInput extends SuggestionInput {
  /** The typed screen, or `undefined` when the panel was opened from a screen
   * this app does not describe. Never widens anything — see rule 3. */
  readonly screen: ScreenContext | undefined;
}

interface ScreenCandidate {
  readonly id: string;
  readonly kind: ScreenKind;
  /** Any ONE of these being advertised makes the suggestion honest. */
  readonly anyTool: readonly string[];
  /** The sentence, or null when it cannot be phrased truthfully for this caller. */
  readonly build: (input: SuggestionInput) => string | null;
}

/**
 * The screen catalog, in the order a person reads it. Tool ids are the live
 * registry's (`apps/api/src/domains/intelligence/tools/`), and every sentence
 * below is answerable by the tool that gates it:
 *
 *   - `supervision.overview` / `supervision.status` scope themselves — the
 *     organization for a `supervision:manage` holder, the caller's own row for
 *     everybody else. So the compliance question is offered only to the arm of
 *     that fork it is true for.
 *   - `analytics.query_metric` answers the catalog in
 *     `apps/api/src/domains/dashboard/metricCatalog.ts`. `shifts_scheduled`,
 *     `no_shows`, `completion_rate` and `schedule_by_worker` are all in it,
 *     which is what makes the schedule and worker questions below real. There
 *     is no mileage metric in that catalog, which is why there is no mileage
 *     candidate here.
 *   - `dashboard.create` / `dashboard.plan` are advertised only to a
 *     `dashboard:manage` holder, so the tool gate IS the permission gate.
 *   - `automation.plan` / `automation.create` gate on three tiers, and naming
 *     the wrong tier offers reach the caller does not hold — so the phrasing
 *     forks the same way the general catalog's does.
 */
const SCREEN_CANDIDATES: readonly ScreenCandidate[] = [
  {
    id: 'screen-worker-compliance',
    kind: 'worker',
    anyTool: ['supervision.overview', 'supervision.status'],
    build: ({ can }) =>
      can('supervision:manage') ? 'Why is this worker showing as a compliance risk?' : null,
  },
  {
    id: 'screen-worker-shifts',
    kind: 'worker',
    anyTool: ['analytics.query_metric'],
    build: () => 'How many shifts is this worker scheduled for this month?',
  },
  {
    id: 'screen-schedule-coverage',
    kind: 'schedule',
    anyTool: ['analytics.query_metric'],
    build: () => 'How many shifts are scheduled this week, and how many were missed?',
  },
  {
    id: 'screen-schedule-supervision',
    kind: 'schedule',
    anyTool: ['supervision.overview'],
    build: ({ can }) =>
      can('supervision:manage')
        ? 'Is anyone on this week’s schedule overdue for supervision?'
        : null,
  },
  {
    id: 'screen-dashboard-add',
    kind: 'dashboard',
    anyTool: ['dashboard.plan', 'dashboard.create'],
    build: () => 'Add this month’s hours by program to this dashboard.',
  },
  {
    id: 'screen-dashboard-metrics',
    kind: 'dashboard',
    anyTool: ['dashboard.list_metrics'],
    build: () => 'What else could I measure on this dashboard?',
  },
  {
    id: 'screen-automation-another',
    kind: 'automation',
    anyTool: ['automation.plan', 'automation.create'],
    build: ({ can }) => {
      // The three tiers the automation tools gate on, strongest first — the
      // same fork the general catalog makes, asked from the rules screen.
      if (can('rules:configure')) return 'Set up another organization-wide alert like these.';
      if (can('automation:manage_team')) return 'Set up another alert for my team like these.';
      if (can('automation:manage_own')) return 'Set up another alert just for me like these.';
      return null;
    },
  },
  // ---------------------------------------------------------------------
  // `mileage` HAS NO CANDIDATE, AND THAT IS THE DECISION, NOT AN OMISSION.
  //
  // F226 asked the question directly: add a mileage measure, or keep the
  // silence and say why. Verified against trunk before answering — there is
  // no mileage key in `apps/api/src/domains/dashboard/metricCatalog.ts`, no
  // mileage resolver in `widgetDataService.ts`, and no `mileage.*` tool in the
  // intelligence registry. `analytics.query_metric` can only answer keys the
  // catalog carries, so today nothing in the runtime can answer a mileage
  // question at all.
  //
  // KEEPING THE SILENCE, because the cheap version of the alternative is the
  // harmful one. A mileage measure is not one line in a catalog: it is a
  // resolver, a scoped repository query, an org-wide permission key and a
  // site-dimension ruling, all in the dashboard domain. Shipping the catalog
  // entry without them would give the executor a key it cannot resolve — and
  // then this file would offer "How has mileage trended?" to a person who
  // gets a refusal back, which reads as the assistant being broken. Offering
  // nothing is not the worse outcome; it is the honest one, and it is rule 2
  // in the header applied to the one screen that tests it.
  //
  // WHEN IT CHANGES: `intelligence-global-starters` reads the server catalog
  // and fails the moment a real mileage measure lands there, with a message
  // saying to write this candidate. The absence maintains itself until it
  // stops being true.
];

/**
 * The starters this caller may honestly be offered on this screen: screen
 * candidates first, then the general catalog, capped at
 * {@link MAX_SUGGESTIONS} in total and de-duplicated by id.
 *
 * An unknown tool list returns `[]` before anything else runs, so this cannot
 * offer a screen starter on a caller whose authority has not come back.
 */
export function deriveScreenSuggestions(input: ScreenSuggestionInput): Suggestion[] {
  const advertised = input.advertisedToolIds;
  if (advertised === undefined) return [];

  const open = new Set(advertised);
  const out: Suggestion[] = [];
  const { screen } = input;

  if (screen !== undefined) {
    for (const candidate of SCREEN_CANDIDATES) {
      if (out.length >= MAX_SUGGESTIONS) break;
      if (candidate.kind !== screen.kind) continue;
      if (!candidate.anyTool.some((toolId) => open.has(toolId))) continue;
      const text = candidate.build(input);
      if (text === null) continue;
      out.push({ id: candidate.id, text });
    }
  }

  // The general catalog, in ITS priority order, filling what is left. Called
  // rather than copied: there is exactly one place that decides what an
  // account may be offered, and this is not it.
  for (const suggestion of deriveSuggestions(input)) {
    if (out.length >= MAX_SUGGESTIONS) break;
    if (out.some((existing) => existing.id === suggestion.id)) continue;
    out.push(suggestion);
  }

  return out;
}
